
import {
  defineComponent,
  useContext,
  ref,
  computed,
} from '@nuxtjs/composition-api'
import Vue from 'vue'

import LazyHydrate from 'vue-lazy-hydration'
import CatalogPagination from './CatalogPagination.vue'
import CatalogFilters from './CatalogFilters.vue'
import CatalogFilterSortBar from './CatalogFilterSortBar.vue'
import CatalogFilterStatusBar from './CatalogFilterStatusBar.vue'
import CatalogSort from './CatalogSort.vue'
import CatalogItems from './CatalogItems.vue'
import CatalogHeader from './CatalogHeader.vue'
import CatalogFooter from './CatalogFooter.vue'
import { useWishlist } from '../../composables/useWishlist'
import { useBudgetFilter } from '../../composables/useBudgetFilter'
import { DaoCatalogs } from '../../assets/ts/catalogs/dao/catalogs'
import { getCountryCode } from '../../assets/ts/Locale'

type catalogStructureItemsPrice = {
  gold: number
  platinum: number
}

type catalogStructureItems = {
  setId: string
  price: catalogStructureItemsPrice
  colors: {
    height: string
    itemNumbers: string[]
    leader: boolean
    metal: string
    setId: string
    stoneQuality: string
  }[]
  tags: object
  display: boolean
  refIdx: number
  displayimage: string
  currentColor: number
  promotion: boolean
}

type catalogStructure = {
  name: string
  slug: string
  catalog: string
  local: object
  items: Array<catalogStructureItems>
  filter: any
  filterColorCode: string
}

export default defineComponent({
  head: {},
  components: {
    LazyHydrate,
    CatalogPagination,
    CatalogFilters,
    CatalogFilterSortBar,
    CatalogFilterStatusBar,
    CatalogSort,
    CatalogItems,
    CatalogHeader,
    CatalogFooter,
  },
  methods: {},
  props: {
    pageroute: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const {
      app: { i18n, router },
    } = useContext()

    const pageRoute = props.pageroute
    let pageName = ''
    let pageStr = ''

    const country: string = getCountryCode()

    if (pageRoute.search('___') !== -1) {
      pageName = pageRoute.substr(0, pageRoute.length - 5)

      if (pageName.search('___') !== -1) {
        pageName = pageName.substr(0, pageName.length - 5)
      }

      pageStr = 'pages.' + pageName
    }

    /* Layout Data */

    const layout = ref({ filterForm: false, sortForm: false })

    /* Catalog Data */

    const catalog = ref({} as catalogStructure)

    /* Catalog Data */
    if (typeof router === 'object') {
      /*
       * Change Catalog Data Pagename for Budget Catalogs
       * If "Budget" is in Catalog Name, the Main Catalog will be loaded for Filtering
       */

      let pageNameForCatalogData = pageName

      if (pageNameForCatalogData.includes('Budget') === true) {
        pageNameForCatalogData = pageNameForCatalogData.substr(
          0,
          pageNameForCatalogData.search('Budget')
        )
      }

      new DaoCatalogs()
        .get(getCountryCode() as string, pageNameForCatalogData)
        .then((data: any) => {
          catalog.value = data as catalogStructure
        })
    }

    /* Filter */

    const filterColor = ref('')
    const filterWidth = ref('')
    const filterStones = ref('')

    const visibleFilterSort = ref('')

    const toggleFilterSort = (node: string) => {
      visibleFilterSort.value = visibleFilterSort.value !== node ? node : ''
    }

    const closeFilterSort = () => {
      visibleFilterSort.value = ''
    }

    const setFilter = (filterType: string, filterValue: string) => {
      if (filterType === 'color') {
        filterColor.value = filterValue
      } else if (filterType === 'width') {
        filterWidth.value = filterValue
      } else if (filterType === 'stones') {
        filterStones.value = filterValue
      }

      closeFilterSort()
    }

    const removeFilter = (filterType: string) => {
      if (filterType === 'color') {
        filterColor.value = ''
      } else if (filterType === 'width') {
        filterWidth.value = ''
      } else if (filterType === 'stones') {
        filterStones.value = ''
      } else if (filterType === 'all') {
        filterColor.value = ''
        filterWidth.value = ''
        filterStones.value = ''
      }
    }

    const numberOfActiveFilter = computed(() => {
      let count = 0

      if (filterColor.value !== '') {
        count++
      }

      if (filterWidth.value !== '') {
        count++
      }

      if (filterStones.value !== '') {
        count++
      }

      return count
    })

    /* Predefined Filter via Hash */
    const setFilterByHash = (routeHash: string) => {
      const hash = routeHash.substr(1)

      const validFilterHashes = [
        'color-g',
        'color-e',
        'color-d',
        'color-r',
        'color-x',
        'color-w',
      ]

      if (validFilterHashes.includes(hash)) {
        const hashSplit = hash.split('-')
        const filterType = hashSplit[0]
        const filterValue = hashSplit[1]

        setFilter(filterType, filterValue)
      }
    }

    if (router && router.currentRoute.hash !== '') {
      setFilterByHash(router.currentRoute.hash)
    }

    /* Budget Filter */
    const { filterBudget, initFromRoute, setBudget } = useBudgetFilter()
    if (
      router &&
      router.currentRoute.name !== undefined &&
      router.currentRoute.name !== null &&
      router.currentRoute.name !== ''
    ) {
      initFromRoute(router.currentRoute.name)
    }
    /* Budget Filter End */

    /* Sort */

    const defaultSortType = 'recommendation'
    const sortType = ref(defaultSortType)

    const setSort = (sort: string) => {
      sortType.value = sort
      toggleFilterSort('sortForm')
    }

    const removeSort = () => {
      sortType.value = 'recommendation'
    }

    /* Items count used for pagination */
    const numberOfActiveItems = ref(0)

    /* Paginantion */

    const itemsPerPage = 24
    const totalPages = computed(() => {
      if (
        numberOfActiveItems.value &&
        numberOfActiveItems.value > itemsPerPage
      ) {
        return Math.ceil(numberOfActiveItems.value / itemsPerPage)
      }
      return 1
    })

    const currentPage = ref(1)
    const maxPages = 9 /* Number of Pages to Display in Frontend Pagination */

    const setNextPage = () => {
      currentPage.value++

      if (currentPage.value > totalPages.value) {
        currentPage.value = 1
      }
      scrollTopCatalog()
    }

    const setPrevPage = () => {
      currentPage.value--

      if (currentPage.value < 1) {
        currentPage.value = totalPages.value
      }
      scrollTopCatalog()
    }

    const setPage = (pageNumber: number) => {
      currentPage.value = pageNumber
      scrollTopCatalog()
    }

    const catalogWrapperPosition = ref(0)

    const scrollTopCatalog = () => {
      catalogWrapperPosition.value = 220 // mobile

      if (typeof window !== 'undefined' && window.innerWidth > 768) {
        catalogWrapperPosition.value = 250 // Desktop
      }

      document.documentElement.scrollTop = catalogWrapperPosition.value // Desktop
    }

    const catalogEmotionImages: Record<string, string> = (() => {
      const ctx = (require as any).context(
        '~/assets/img/catalog',
        false,
        /\.(webp)$/
      )
      const map: Record<string, string> = {}
      ctx
        .keys()
        .map((key: string) => key.replace('./', ''))
        // Only keep files that start with the current pageName
        .filter((fileName: string) =>
          pageName ? fileName.startsWith(`${pageName}-`) : true
        )
        .forEach((fileName: string) => {
          map[fileName] = ctx(`./${fileName}`)
        })
      return map
    })()

    /* Items */

    const filteredItems = computed(() => {
      const returnData: Array<any> = []

      if (typeof catalog.value.items !== 'undefined') {
        for (const catalogItemIdx in catalog.value.items) {
          const catalogItem = catalog.value.items[catalogItemIdx]

          catalogItem.display = false
          catalogItem.refIdx = parseInt(catalogItemIdx)

          // color
          if (
            filterColor.value === '' ||
            typeof filterColor.value === 'undefined'
          ) {
            for (const colorIdx in catalogItem.colors) {
              if (catalogItem.colors[colorIdx].leader === true) {
                catalogItem.displayimage = catalogItem.colors[colorIdx].setId
                catalogItem.display = true
              }
            }
          } else {
            for (const colorIdx in catalogItem.colors) {
              if (
                catalogItem.colors[colorIdx].metal
                  .charAt(0)
                  .toLowerCase()
                  .trim() === filterColor.value
              ) {
                catalogItem.displayimage = catalogItem.colors[colorIdx].setId
                catalogItem.display = true
                // Ensure the active color context (link, badges) matches the filtered color
                try {
                  const idx = parseInt(colorIdx)
                  if (!isNaN(idx)) {
                    Vue.set(catalogItem, 'currentColor', idx)
                  }
                } catch (e) {
                  // ignore
                }
                // Stop at first matching color to keep state stable
                break
              }
            }
          }

          // width
          if (
            filterWidth.value !== '' &&
            typeof filterWidth.value !== 'undefined'
          ) {
            if (catalogItem.display === true) {
              if (
                Object.values(catalogItem.tags).includes(filterWidth.value) ===
                false
              ) {
                catalogItem.display = false
              }
            }
          }

          // stones
          if (
            filterStones.value !== '' &&
            typeof filterStones.value !== 'undefined'
          ) {
            if (catalogItem.display === true) {
              if (
                Object.values(catalogItem.tags).includes(filterStones.value) ===
                false
              ) {
                catalogItem.display = false
              }
            }
          }

          // Budget
          if (filterBudget.value.status === true) {
            if (
              catalogItem.display === true &&
              (catalogItem.price.gold < filterBudget.value.start ||
                catalogItem.price.gold > filterBudget.value.end)
            ) {
              catalogItem.display = false
            }
          }

          // push data for return
          if (catalogItem.display === true) {
            /* Promotion for special items */
            catalogItem.promotion = false
            if (['de', 'at', 'ch', 'nl'].includes(country)) {
              if (['A-2098-2', 'A-2896-2'].includes(catalogItem.displayimage)) {
                catalogItem.promotion = true
              }
            }

            returnData.push(catalogItem)
          }
        }
      }

      // Set new number of Active Items

      numberOfActiveItems.value = 0

      if (returnData.length > 0) {
        numberOfActiveItems.value = returnData.length
      }

      // If Result is empty, change or remove filter
      if (numberOfActiveItems.value === 0) {
        if (filterColor.value !== '') {
          filterColor.value = 'w'
        }

        if (filterWidth.value !== '') {
          filterWidth.value = ''
        }

        if (filterStones.value !== '') {
          filterStones.value = ''
        }
      }

      /* Sort Data */
      if (sortType.value === 'priceAsc' || sortType.value === 'priceDesc') {
        if (sortType.value === 'priceAsc') {
          returnData.sort(function (a, b) {
            return a.price.gold - b.price.gold
          })
        } else if (sortType.value === 'priceDesc') {
          returnData.sort(function (a, b) {
            return b.price.gold - a.price.gold
          })
        }
      }

      /* Pagination */

      let paginationStart = 0
      let paginationEnd = itemsPerPage

      if (currentPage.value > 1) {
        paginationStart = itemsPerPage * (currentPage.value - 1)
        paginationEnd = paginationStart + itemsPerPage
      }

      const slice = returnData.slice(paginationStart, paginationEnd)

      /* Add Emotion Banner Start */

      const emotionImages = [
        'design',
        'ethical',
        'style',
        'germany',
        'customization',
      ]

      /* Check if custom Emotion Image for current catalog exists in assets folder and add it to the beginning of the list */
      const emotionCandidates = [
        `${pageName}-emotion-01@2x.webp`,
        `${pageName}-emotion-02@2x.webp`,
        `${pageName}-emotion-03@2x.webp`,
        `${pageName}-emotion-04@2x.webp`,
        `${pageName}-emotion-05@2x.webp`
      ]

      const foundCustoms = emotionCandidates.filter(
        (f) => !!catalogEmotionImages[f]
      )
      if (foundCustoms.length) {
        // Prepend all found custom images in the given order
        emotionImages.unshift(...foundCustoms)
      }

      const emotionImagesList: string[] = []
      let x = 0
      for (let i = 0; i < 20; i++) {
        for (const emotion of emotionImages) {
          emotionImagesList[x] = emotion
          x++
        }
      }

      const image1 = emotionImagesList[currentPage.value * 2 - 2]
      const image2 = emotionImagesList[currentPage.value * 2 - 1]

      const position1 = 6
      const position2 = 17

      slice.splice(position1, 0, {
        image: image1,
        colspan: true,
        rowspan: true,
      })

      if (returnData.length > position2 - 1) {
        slice.splice(position2, 0, {
          image: image2,
          colspan: true,
          rowspan: true,
        })
      }

      return slice
    })
    /* Add Emotion Banner End */

    /* Item Color */

    const changeItemColor = (colorIdx: number, catalogItemIdx: number) => {
      /* Current Color */
        Vue.set(catalog.value.items[catalogItemIdx], 'currentColor', colorIdx)

      /* Current Item Image */
        Vue.set(
          catalog.value.items[catalogItemIdx],
          'displayimage',
          catalog.value.items[catalogItemIdx].colors[colorIdx].setId
        )

      /* Increase Counter for reactive DOM Changes */
      changeCounter.value++
    }

    const changeCounter = ref(1)

    /** Wishlist via composable */
    const { wishlistItems, add, remove, load } = useWishlist()
    load()
    const addToWishlist = (itemSlug: string) => add(itemSlug)
    const removeFromWishlist = (itemSlug: string) => remove(itemSlug)


    const imagesHost: string = process.env.imagesHost as string

    return {
      pageRoute,
      pageName,
      pageStr,
      i18n,
      layout,
      catalog,
      country,
      changeItemColor,
      visibleFilterSort,
      toggleFilterSort,
      setFilter,
      removeFilter,
      filterColor,
      filterWidth,
      filterStones,
      numberOfActiveFilter,
      setSort,
      removeSort,
      sortType,
      numberOfActiveItems,
      filteredItems,
      changeCounter,
      currentPage,
      totalPages,
      itemsPerPage,
      maxPages,
      setNextPage,
      setPrevPage,
      setPage,
      filterBudget,
      setBudget,
      wishlistItems,
      addToWishlist,
      removeFromWishlist,
      imagesHost,
    }
  },
})
